import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import api from '../lib/api';

/**
 * ProtectedRoute Component
 * 
 * Features:
 * 1. Authentication check - redirects to /login if not authenticated
 * 2. Onboarding check - redirects to /onboarding/brain-init only if brand new user
 * 3. adminOnly (optional) - redirects non company-admins to /dashboard
 */
export const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const { isAdmin } = usePermissions();
  const location = useLocation();
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [onboardingLoading, setOnboardingLoading] = useState(true);

  useEffect(() => {
    const checkOnboarding = async () => {
      if (!isAuthenticated || location.pathname.startsWith('/onboarding')) {
        setOnboardingLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('access_token');
        if (!token) {
          setOnboardingStatus({ onboarding_completed: true });
          setOnboardingLoading(false);
          return;
        }
        const response = await api.get('/api/onboarding/status', { timeout: 8000 });
        
        if (response.data) {
          setOnboardingStatus(response.data);
        } else {
          setOnboardingStatus({ onboarding_completed: true });
        }
      } catch (error) {
        console.error('Failed to check onboarding status:', error);
        setOnboardingStatus({ onboarding_completed: true });
      } finally {
        setOnboardingLoading(false);
      }
    };

    if (isAuthenticated && !authLoading) {
      checkOnboarding();
    } else if (!authLoading) {
      setOnboardingLoading(false);
    }
  }, [isAuthenticated, authLoading, location.pathname]);

  if (authLoading || onboardingLoading) {
    return (
      <div className="min-h-screen flex" data-testid="loading-screen">
        {/* Sidebar skeleton */}
        <div className="w-16 border-r border-border bg-card flex flex-col gap-4 p-3 pt-6 shrink-0">
          <div className="h-8 w-8 rounded-xl bg-muted animate-pulse mx-auto" />
          <div className="space-y-4 mt-4">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="h-8 w-8 rounded-lg bg-muted animate-pulse mx-auto" />
            ))}
          </div>
        </div>
        {/* Main content skeleton */}
        <div className="flex-1 p-6 lg:p-8 space-y-6">
          {/* Header skeleton */}
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <div className="h-5 w-40 rounded-md bg-muted animate-pulse" />
              <div className="h-3 w-24 rounded-md bg-muted animate-pulse" />
            </div>
            <div className="h-9 w-9 rounded-full bg-muted animate-pulse" />
          </div>
          {/* Tab skeleton */}
          <div className="flex justify-center">
            <div className="h-10 w-56 rounded-lg bg-muted animate-pulse" />
          </div>
          {/* Prompt bar skeleton */}
          <div className="max-w-3xl mx-auto space-y-6 mt-8">
            <div className="h-14 w-full rounded-2xl bg-muted animate-pulse" />
            {/* Suggestion pills skeleton */}
            <div className="flex gap-2 justify-center flex-wrap">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-8 rounded-full bg-muted animate-pulse" style={{ width: `${80 + i * 12}px` }} />
              ))}
            </div>
            {/* Card skeletons */}
            <div className="space-y-3 mt-4">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="h-20 w-full rounded-xl bg-muted animate-pulse" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Check localStorage as fallback - token might exist but React context hasn't updated yet
    // This prevents redirect loops when navigating right after login
    const token = localStorage.getItem('access_token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      // Token exists in localStorage but context isn't updated yet - show loading
      return (
        <div className="min-h-screen flex" data-testid="auth-sync-loading">
          <div className="w-16 border-r border-border bg-card flex flex-col gap-4 p-3 pt-6 shrink-0">
            <div className="h-8 w-8 rounded-xl bg-muted animate-pulse mx-auto" />
            <div className="space-y-4 mt-4">
              {[...Array(7)].map((_, i) => (
                <div key={i} className="h-8 w-8 rounded-lg bg-muted animate-pulse mx-auto" />
              ))}
            </div>
          </div>
          <div className="flex-1 p-6 lg:p-8 flex items-center justify-center">
            <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
          </div>
        </div>
      );
    }
    return <Navigate to="/login" replace />;
  }

  // Only redirect to onboarding if user has NEVER started it.
  // Phase 3: icp_ready is the "early sufficient" signal — user can enter the
  // app while Apollo leads are still landing in the background.
  if (
    !location.pathname.startsWith('/onboarding') &&
    onboardingStatus &&
    !onboardingStatus.onboarding_completed &&
    !onboardingStatus.onboarding_skipped &&
    !onboardingStatus.brain_data_seeded &&
    !onboardingStatus.icp_ready &&
    (!onboardingStatus.selected_modules || onboardingStatus.selected_modules.length === 0)
  ) {
    return <Navigate to="/onboarding/brain-init" replace />;
  }

  // Company-admin gate (e.g. LEO Impact ROI). Mirrors AppShell's admin check so a
  // tenant owner is allowed while a regular employee is redirected away.
  if (adminOnly) {
    const canSeeAdmin = user?.role === 'tenant_admin' || user?.role === 'org_admin' || isAdmin();
    if (!canSeeAdmin) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

export default ProtectedRoute;
